/**
 * Test card form for the backend FakeProcessor (provider "FAKE"; tests,
 * development with ONLINE_PAYMENTS_PROVIDER=fake, and the mock API). It
 * loads no script and collects no card data: the user picks an outcome and
 * tokenize() returns `fake-tok-<outcome>-<random>`, which the fake processor
 * interprets. Never used against a real processor.
 */
import { PaymentClientConfig } from '../../../../api/types';
import { PaymentFormAdapter, PaymentFormError } from '../paymentFormAdapter';

export const FAKE_PROVIDER = 'FAKE';
export const FAKE_OUTCOMES = [
  { value: 'ok', label: 'Approved' },
  { value: 'decline', label: 'Declined' },
  { value: 'insufficient', label: 'Insufficient funds' },
  { value: 'error', label: 'No answer (outcome unknown)' },
] as const;

export class FakePaymentFormAdapter implements PaymentFormAdapter {
  readonly provider = FAKE_PROVIDER;
  private select: HTMLSelectElement | null = null;
  private loaded = false;

  async load(clientConfig: PaymentClientConfig): Promise<void> {
    if (clientConfig.provider !== FAKE_PROVIDER) throw new PaymentFormError('Wrong card form for this processor.');
    this.loaded = true;
  }

  async render(container: HTMLElement): Promise<void> {
    if (!this.loaded) throw new PaymentFormError('The card form is not loaded.');
    const label = document.createElement('label');
    label.className = 'block text-sm text-gray-700';
    label.textContent = 'Test card (no real card is charged)';
    const select = document.createElement('select');
    select.className = 'mt-1 block w-full rounded border border-gray-300 px-3 py-2';
    select.setAttribute('aria-label', 'Test card outcome');
    for (const o of FAKE_OUTCOMES) {
      const option = document.createElement('option');
      option.value = o.value;
      option.textContent = o.label;
      select.appendChild(option);
    }
    label.appendChild(select);
    container.appendChild(label);
    this.select = select;
  }

  async tokenize(): Promise<{ sourceToken: string }> {
    if (!this.select) throw new PaymentFormError('The card form is not ready.');
    return { sourceToken: `fake-tok-${this.select.value}-${Math.random().toString(36).slice(2, 12)}` };
  }

  destroy(): void {
    this.select?.closest('label')?.remove();
    this.select = null;
    this.loaded = false;
  }
}

export const fakeRegistration = { provider: FAKE_PROVIDER, create: () => new FakePaymentFormAdapter() };
